import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { extractErrorMessage } from '../../shared/utils/errors';
import {
  FinanceOperation,
  PaginatedResponse,
  PoolWallet,
  User,
  UserWalletGroup,
  WalletDetail,
  WalletOverview,
} from '../../shared/types';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';
import { useAuth } from '../auth/AuthContext';
import {
  PoolImportPreview,
  ScanResult,
  WalletFilters,
  assignWalletToUser,
  getWallet,
  importPoolBatch,
  listPoolWallets,
  listWallets,
  previewPoolImport,
  scanWalletDeposits,
  setWalletActive,
} from './api';
import { listOperations } from '../finance/api';
import { getUsers } from '../users/api';
import { formatNetworkLabel } from '../../shared/utils/networkLabel';
import { POOL_CURRENCIES, getPoolCurrencyConfig } from '../../shared/utils/walletAddress';
import PoolWalletsSection from './PoolWalletsSection';
import DepositBatchesSection from './DepositBatchesSection';
import DepositSignersSection from './DepositSignersSection';

type WalletsTab = 'assigned' | 'pool' | 'batches' | 'signers';

const WALLET_TABS: [WalletsTab, string][] = [
  ['assigned', 'User wallets'],
  ['pool', 'Deposit pool'],
  ['batches', 'Import batches'],
  ['signers', 'Signing keys'],
];

const CURRENCY_OPTIONS = ['', 'BTC', 'ETH', 'USDT', 'TRX', 'USD'];

const SMALLEST_UNIT_DIVISOR = 100_000_000;

const formatCryptoAmount = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '0';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(value)) return '0';
  const decimal = value / SMALLEST_UNIT_DIVISOR;
  return decimal.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const SCAN_SUPPORTED_PAIRS = new Set(['USDT:TRC20', 'ETH:ETH', 'TRX:TRON', 'BTC:BTC']);

const isScanSupported = (wallet: WalletOverview): boolean => {
  if (!wallet.id || !wallet.address || !wallet.currency || !wallet.network) return false;
  return SCAN_SUPPORTED_PAIRS.has(
    `${wallet.currency.toUpperCase()}:${wallet.network.toUpperCase()}`
  );
};

interface ScanState {
  walletAddress: string;
  walletCurrency: string;
  result: ScanResult | null;
  error: string | null;
  loading: boolean;
}

const WalletsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canScan = hasPermission('ledger.scan_deposits');
  const canAddWallet = hasPermission('ledger.add_wallets');
  const canManage = hasPermission('ledger.manage_wallets');
  const canRevokeSigners = hasPermission('ledger.revoke_signers');
  const canViewOperations = hasPermission('finance.view_operations');

  const [wallets, setWallets] = useState<PaginatedResponse<UserWalletGroup> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<WalletFilters>({});
  const [draftFilters, setDraftFilters] = useState<WalletFilters>({});

  const [detail, setDetail] = useState<WalletDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [scanningWalletId, setScanningWalletId] = useState<number | null>(null);
  const [scanModal, setScanModal] = useState<ScanState | null>(null);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [poolCurrency, setPoolCurrency] = useState<string>('USDT');
  const [poolNetwork, setPoolNetwork] = useState<string>('TRC20');
  const [importJws, setImportJws] = useState('');
  const [importPreview, setImportPreview] = useState<PoolImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WalletsTab>('assigned');
  const [poolRefreshKey, setPoolRefreshKey] = useState(0);

  const [toggleTarget, setToggleTarget] = useState<WalletOverview | null>(null);
  const [toggleSubmitting, setToggleSubmitting] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignUserLocked, setAssignUserLocked] = useState(false);
  const [assignUserLabel, setAssignUserLabel] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userOptionsLoading, setUserOptionsLoading] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [assignCurrency, setAssignCurrency] = useState('USDT');
  const [assignNetwork, setAssignNetwork] = useState('TRC20');
  const [assignWalletId, setAssignWalletId] = useState('');
  const [assignPool, setAssignPool] = useState<PoolWallet[]>([]);
  const [assignPoolLoading, setAssignPoolLoading] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);

  const [opsWallet, setOpsWallet] = useState<WalletOverview | null>(null);
  const [opsPage, setOpsPage] = useState(1);
  const [opsData, setOpsData] = useState<PaginatedResponse<FinanceOperation> | null>(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsError, setOpsError] = useState<string | null>(null);

  const load = useCallback(async (page: number, filtersToUse: WalletFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listWallets({ ...filtersToUse, page, per_page: 20 });
      setWallets(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load wallets'));
      setWallets(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  useEffect(() => {
    if (!assignOpen) return;
    let cancelled = false;
    setAssignPoolLoading(true);
    listPoolWallets({ currency: assignCurrency, per_page: 100 })
      .then((res) => {
        if (!cancelled) setAssignPool(res.data);
      })
      .catch(() => {
        if (!cancelled) setAssignPool([]);
      })
      .finally(() => {
        if (!cancelled) setAssignPoolLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assignOpen, assignCurrency]);

  useEffect(() => {
    if (!assignOpen || assignUserLocked) return;
    let cancelled = false;
    setUserOptionsLoading(true);
    const timer = window.setTimeout(() => {
      getUsers({ search: userQuery.trim() || undefined, perPage: 20 })
        .then((res) => {
          if (!cancelled) setUserOptions(res.data);
        })
        .catch(() => {
          if (!cancelled) setUserOptions([]);
        })
        .finally(() => {
          if (!cancelled) setUserOptionsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [assignOpen, assignUserLocked, userQuery]);

  useEffect(() => {
    if (!opsWallet?.id) return;
    let cancelled = false;
    setOpsLoading(true);
    setOpsError(null);
    listOperations({ wallet_id: opsWallet.id, page: opsPage, per_page: 10 })
      .then((res) => {
        if (!cancelled) setOpsData(res);
      })
      .catch((err) => {
        if (!cancelled) setOpsError(extractErrorMessage(err, 'Failed to load operations'));
      })
      .finally(() => {
        if (!cancelled) setOpsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opsWallet, opsPage]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const openDetail = async (wallet: WalletOverview) => {
    if (!wallet.id) return;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const fresh = await getWallet(wallet.id);
      setDetail(fresh);
    } catch (err) {
      setDetailError(extractErrorMessage(err, 'Failed to load wallet'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setDetailError(null);
  };

  const runScan = async (wallet: WalletOverview) => {
    if (!wallet.id || !wallet.address) return;
    setScanningWalletId(wallet.id);
    setScanModal({
      walletAddress: wallet.address,
      walletCurrency: wallet.currency,
      result: null,
      error: null,
      loading: true,
    });
    try {
      const result = await scanWalletDeposits(wallet.id, wallet.address);
      setScanModal({
        walletAddress: wallet.address,
        walletCurrency: wallet.currency,
        result,
        error: null,
        loading: false,
      });
      if (result.credited > 0) {
        load(currentPage, filters);
      }
    } catch (err) {
      setScanModal({
        walletAddress: wallet.address,
        walletCurrency: wallet.currency,
        result: null,
        error: extractErrorMessage(err, 'Scan failed'),
        loading: false,
      });
    } finally {
      setScanningWalletId(null);
    }
  };

  const closeScanModal = () => {
    setScanModal(null);
  };

  const openImportModal = () => {
    const config = getPoolCurrencyConfig('USDT');
    setPoolCurrency('USDT');
    setPoolNetwork(config.networks[0].value);
    setImportJws('');
    setImportPreview(null);
    setImportError(null);
    setImportSuccess(null);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportError(null);
    setImportSuccess(null);
  };

  const onImportCurrencyChange = (value: string) => {
    const config = getPoolCurrencyConfig(value);
    setPoolCurrency(config.currency);
    setPoolNetwork(config.networks[0].value);
    setImportPreview(null);
  };

  const onImportJwsChange = (value: string) => {
    setImportJws(value);
    setImportPreview(null);
    setImportError(null);
    setImportSuccess(null);
  };

  const runImportPreview = async () => {
    if (!importJws.trim()) return;
    setPreviewLoading(true);
    setImportError(null);
    setImportSuccess(null);
    try {
      setImportPreview(
        await previewPoolImport({ jws: importJws, currency: poolCurrency, network: poolNetwork })
      );
    } catch (err) {
      setImportPreview(null);
      setImportError(extractErrorMessage(err, 'Signed batch was rejected'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const submitImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importPreview || importPreview.errors.length > 0) return;
    setImportSubmitting(true);
    setImportError(null);
    setImportSuccess(null);
    try {
      const result = await importPoolBatch({
        jws: importJws,
        currency: poolCurrency,
        network: poolNetwork,
      });
      setImportSuccess(
        `Batch #${result.batch_id} imported: ${result.new} new address${result.new === 1 ? '' : 'es'}` +
          (result.link > 0 ? `, ${result.link} existing linked` : '') +
          (result.exists > 0 ? `, ${result.exists} already signed` : '') +
          '.'
      );
      setImportJws('');
      setImportPreview(null);
      setPoolRefreshKey((k) => k + 1);
      setActiveTab('pool');
      load(currentPage, filters);
    } catch (err) {
      setImportError(extractErrorMessage(err, 'Import failed'));
    } finally {
      setImportSubmitting(false);
    }
  };

  const openToggle = (wallet: WalletOverview) => {
    setToggleTarget(wallet);
    setToggleError(null);
  };

  const closeToggle = () => {
    setToggleTarget(null);
    setToggleError(null);
  };

  const confirmToggle = async () => {
    if (!toggleTarget?.id) return;
    const nextActive = toggleTarget.is_active === false;
    setToggleSubmitting(true);
    setToggleError(null);
    try {
      await setWalletActive(toggleTarget.id, nextActive);
      closeToggle();
      load(currentPage, filters);
    } catch (err) {
      setToggleError(extractErrorMessage(err, 'Failed to update wallet status'));
    } finally {
      setToggleSubmitting(false);
    }
  };

  const openAssignModal = (userId?: number) => {
    const config = getPoolCurrencyConfig('USDT');
    setAssignUserId(userId ? String(userId) : '');
    setAssignUserLocked(Boolean(userId));
    setAssignUserLabel(userId ? formatUserLabel(userId, userNames) : '');
    setUserQuery('');
    setUserOptions([]);
    setUserDropdownOpen(false);
    setAssignCurrency(config.currency);
    setAssignNetwork(config.networks[0].value);
    setAssignWalletId('');
    setAssignError(null);
    setAssignSuccess(null);
    setAssignOpen(true);
  };

  const selectAssignUser = (user: User) => {
    const label = user.username ? `@${user.username}` : user.full_name || user.name;
    setAssignUserId(String(user.id));
    setAssignUserLabel(label);
    setUserQuery(`${label} · #${user.id}`);
    setUserDropdownOpen(false);
  };

  const closeAssignModal = () => {
    setAssignOpen(false);
    setAssignError(null);
    setAssignSuccess(null);
  };

  const onAssignCurrencyChange = (value: string) => {
    const config = getPoolCurrencyConfig(value);
    setAssignCurrency(config.currency);
    setAssignNetwork(config.networks[0].value);
    setAssignWalletId('');
  };

  const submitAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    const uid = parseInt(assignUserId, 10);
    if (!uid || uid < 1) {
      setAssignError('Select a user first');
      return;
    }
    setAssignSubmitting(true);
    setAssignError(null);
    setAssignSuccess(null);
    try {
      const wallet = await assignWalletToUser({
        user_id: uid,
        ...(assignWalletId
          ? { wallet_id: Number(assignWalletId) }
          : { currency: assignCurrency, network: assignNetwork }),
      });
      setAssignSuccess(`Wallet #${wallet.id} (${wallet.address}) assigned to user #${uid}.`);
      setAssignWalletId('');
      setPoolRefreshKey((k) => k + 1);
      load(currentPage, filters);
    } catch (err) {
      setAssignError(extractErrorMessage(err, 'Failed to assign wallet'));
    } finally {
      setAssignSubmitting(false);
    }
  };

  const openOperations = (wallet: WalletOverview) => {
    setOpsData(null);
    setOpsPage(1);
    setOpsWallet(wallet);
  };

  const closeOperations = () => {
    setOpsWallet(null);
    setOpsData(null);
    setOpsError(null);
  };

  const filterByUser = (userId: number) => {
    const value = String(userId);
    setDraftFilters((p) => ({ ...p, user_id: value }));
    setFilters((p) => ({ ...p, user_id: value }));
    setCurrentPage(1);
  };

  const userIds: number[] = [];
  if (wallets?.data) {
    for (const group of wallets.data) userIds.push(group.user_id);
  }
  if (detail?.wallet) userIds.push(detail.wallet.user_id);
  const userNames = useUserNames(userIds);

  const modalOpen = detail !== null || detailLoading || detailError !== null;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Wallets</h1>
            <p className="text-slate-400 mt-1">
              Users with assigned deposit wallets and their balances.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {canManage && (
              <button type="button" onClick={() => openAssignModal()} className="btn btn-secondary">
                Assign to user
              </button>
            )}
            {canAddWallet && (
              <button type="button" onClick={openImportModal} className="btn btn-primary">
                Import signed addresses
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-slate-800">
          {WALLET_TABS.map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-indigo-500 text-white'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'pool' ? (
          <PoolWalletsSection
            canManage={canAddWallet}
            onImportClick={openImportModal}
            refreshKey={poolRefreshKey}
          />
        ) : activeTab === 'batches' ? (
          <DepositBatchesSection refreshKey={poolRefreshKey} />
        ) : activeTab === 'signers' ? (
          <DepositSignersSection
            canRevoke={canRevokeSigners}
            onRevoked={() => {
              setPoolRefreshKey((k) => k + 1);
              load(currentPage, filters);
            }}
          />
        ) : (
          <>
        <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-cur'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Address</label>
            <input
              type="text"
              value={draftFilters.address ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, address: e.target.value || undefined }))
              }
              className="input py-2 font-mono text-xs"
              placeholder="0x… / bc1…"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={draftFilters.is_active ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({
                  ...p,
                  is_active: (e.target.value || undefined) as WalletFilters['is_active'],
                }))
              }
              className="input py-2"
            >
              <option value="">Any</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
          <div className="flex gap-2 lg:col-span-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !wallets?.data.length ? (
            <div className="p-12 text-center text-slate-400">No wallets found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">User</th>
                      <th className="table-header">Currency</th>
                      <th className="table-header">Network</th>
                      <th className="table-header">Address</th>
                      <th className="table-header">Status</th>
                      <th className="table-header text-right">Balance</th>
                      <th className="table-header text-right">Available</th>
                      <th className="table-header text-right">In escrow</th>
                      <th className="table-header text-right">Received</th>
                      <th className="table-header">Assigned</th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {wallets.data.map((group) =>
                      group.wallets.map((wallet, walletIndex) => (
                        <tr key={`${group.user_id}-${wallet.currency}-${wallet.network ?? 'none'}-${wallet.id ?? walletIndex}`} className="hover:bg-slate-800/30 transition-colors">
                          {walletIndex === 0 && (
                            <td
                              className="table-cell align-top border-r border-slate-800/60"
                              rowSpan={group.wallets.length}
                            >
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); filterByUser(group.user_id); }}
                                className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                                title={`Show all wallets for user #${group.user_id}`}
                              >
                                {formatUserLabel(group.user_id, userNames)}
                              </button>
                              <p className="text-xs text-slate-500 font-mono mt-0.5">#{group.user_id}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {group.wallets.length} asset{group.wallets.length === 1 ? '' : 's'}
                              </p>
                              {canManage && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openAssignModal(group.user_id); }}
                                  className="text-xs text-indigo-400 hover:text-indigo-300 hover:underline mt-1"
                                >
                                  + Assign wallet
                                </button>
                              )}
                            </td>
                          )}
                          <td className="table-cell text-white font-medium whitespace-nowrap">
                            {wallet.currency}
                            {wallet.id !== null && (
                              <span className="text-xs text-slate-500 font-mono ml-2">#{wallet.id}</span>
                            )}
                          </td>
                          <td className="table-cell text-slate-300 text-sm whitespace-nowrap">
                            {wallet.network ? formatNetworkLabel(wallet.network) : (
                              <span className="text-slate-500">—</span>
                            )}
                          </td>
                          <td className="table-cell font-mono text-xs text-slate-300 max-w-[200px] truncate" title={wallet.address ?? undefined}>
                            {wallet.address ?? (
                              <span className="text-slate-500 italic">No address</span>
                            )}
                            {wallet.status && wallet.status !== 'assigned' && (
                              <span className="ml-2 badge badge-warning text-[10px]">{wallet.status}</span>
                            )}
                            {wallet.address && (
                              <p
                                className="text-[10px] font-sans text-slate-500 mt-0.5 truncate"
                                title={wallet.signer_kid ?? undefined}
                              >
                                {wallet.signer_kid
                                  ? `signed · ${wallet.signer_label || wallet.signer_kid}`
                                  : 'unsigned (legacy)'}
                              </p>
                            )}
                          </td>
                          <td className="table-cell whitespace-nowrap">
                            {wallet.is_active === false ? (
                              <span className="badge badge-warning text-[10px]" title="Deactivated: not scanned for deposits and hidden from the user's deposit UI">
                                Inactive
                              </span>
                            ) : (
                              <span className="badge badge-success text-[10px]">Active</span>
                            )}
                          </td>
                          <td className="table-cell text-right font-mono text-slate-200">
                            {formatCryptoAmount(wallet.balance)}
                          </td>
                          <td className="table-cell text-right font-mono text-emerald-400">
                            {formatCryptoAmount(wallet.available)}
                          </td>
                          <td className="table-cell text-right font-mono text-amber-400">
                            {formatCryptoAmount(wallet.held)}
                          </td>
                          <td className="table-cell text-right font-mono text-slate-300" title="Total received via this address">
                            {wallet.total_received != null ? formatCryptoAmount(wallet.total_received) : '—'}
                          </td>
                          <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                            {formatDateTime(wallet.assigned_at)}
                          </td>
                          <td className="table-cell text-right">
                            {wallet.id !== null ? (
                              <div className="inline-flex gap-1 justify-end">
                                {canScan && isScanSupported(wallet) && (
                                  <button
                                    type="button"
                                    onClick={() => runScan(wallet)}
                                    disabled={scanningWalletId === wallet.id}
                                    className="btn btn-primary py-1 px-3 text-xs disabled:opacity-60"
                                    title="Scan the blockchain for incoming deposits to this wallet"
                                  >
                                    {scanningWalletId === wallet.id ? 'Scanning…' : 'Scan'}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => openDetail(wallet)}
                                  className="btn btn-secondary py-1 px-3 text-xs"
                                >
                                  Details
                                </button>
                                {canViewOperations && (
                                  <button
                                    type="button"
                                    onClick={() => openOperations(wallet)}
                                    className="btn btn-secondary py-1 px-3 text-xs"
                                    title="Show ledger operations attributed to this address"
                                  >
                                    Operations
                                  </button>
                                )}
                                {canManage && (
                                  <button
                                    type="button"
                                    onClick={() => openToggle(wallet)}
                                    className={`btn py-1 px-3 text-xs ${
                                      wallet.is_active === false ? 'btn-primary' : 'btn-danger'
                                    }`}
                                  >
                                    {wallet.is_active === false ? 'Activate' : 'Deactivate'}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {wallets.last_page > 1 && (
                <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {wallets.total} user{wallets.total === 1 ? '' : 's'}
                  </p>
                  <Pagination
                    currentPage={wallets.current_page}
                    lastPage={wallets.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
          </>
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={closeDetail} title="Wallet details" size="lg">
        {detailLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : detailError ? (
          <p className="text-red-400">{detailError}</p>
        ) : detail ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">User</p>
                <button
                  type="button"
                  onClick={() => { closeDetail(); filterByUser(detail.wallet.user_id); }}
                  className="text-sky-400 hover:underline"
                >
                  {formatUserLabel(detail.wallet.user_id, userNames)}
                </button>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Currency</p>
                <p className="text-white font-medium">{detail.wallet.currency}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-1">Address</p>
                <p className="font-mono text-sm text-slate-200 break-all">{detail.wallet.address}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Network</p>
                <p className="text-slate-200">{formatNetworkLabel(detail.wallet.network)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Assigned at</p>
                <p className="text-slate-200">{formatDateTime(detail.wallet.assigned_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Received via this address</p>
                <p className="font-mono text-slate-200">
                  {detail.wallet.total_received != null
                    ? `${formatCryptoAmount(detail.wallet.total_received)} ${detail.wallet.currency}`
                    : '—'}
                </p>
              </div>
            </div>

            {detail.account && (
              <div className="card p-4 bg-slate-800/40">
                <h3 className="text-sm font-semibold text-white mb-3">Account balance</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="font-mono text-white">{formatCryptoAmount(detail.account.balance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Available</p>
                    <p className="font-mono text-emerald-400">{formatCryptoAmount(detail.account.available)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">In escrow</p>
                    <p className="font-mono text-amber-400">{formatCryptoAmount(detail.account.held)}</p>
                  </div>
                </div>
              </div>
            )}

            {detail.escrows.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Active escrows</h3>
                <div className="space-y-2">
                  {detail.escrows.map((escrow) => (
                    <div key={escrow.id} className="card p-3 bg-slate-800/40 flex justify-between items-center gap-4">
                      <div>
                        <p className="font-mono text-xs text-indigo-400">{escrow.reference_id}</p>
                        {escrow.description && (
                          <p className="text-xs text-slate-400 mt-1">{escrow.description}</p>
                        )}
                      </div>
                      <p className="font-mono text-amber-400 whitespace-nowrap">
                        {formatCryptoAmount(escrow.amount)} {escrow.currency}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={importModalOpen}
        onClose={closeImportModal}
        title="Import signed deposit addresses"
        size="lg"
      >
        <form onSubmit={submitImport} className="space-y-4">
          <p className="text-sm text-slate-400">
            Paste the signed batch (JWS) you received. Addresses can only be added this way: the
            signature must come from a trusted signing key and is re-verified every time an
            address is handed out.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
              <select
                value={poolCurrency}
                onChange={(e) => onImportCurrencyChange(e.target.value)}
                className="input py-2"
                disabled={importSubmitting}
              >
                {POOL_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Network</label>
              <select
                value={poolNetwork}
                onChange={(e) => {
                  setPoolNetwork(e.target.value);
                  setImportPreview(null);
                }}
                className="input py-2"
                disabled={importSubmitting}
              >
                {getPoolCurrencyConfig(poolCurrency).networks.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Signed batch (JWS)</label>
            <textarea
              value={importJws}
              onChange={(e) => onImportJwsChange(e.target.value)}
              className="input py-2 font-mono text-xs min-h-[120px]"
              placeholder="eyJhbGciOiJFUzI1NiIs…"
              autoComplete="off"
              spellCheck={false}
              disabled={importSubmitting}
            />
            <p className="text-xs text-slate-500 mt-1">
              Currency and network are not part of the signature — pick them carefully. Every
              address is validated against the chosen network before anything is saved.
            </p>
          </div>

          {importPreview && (
            <div className="card p-3 bg-slate-800/40 text-xs space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <p>
                  <span className="text-slate-500">Signer:</span>{' '}
                  <span className="text-slate-200">{importPreview.signer_label || '—'}</span>
                </p>
                <p>
                  <span className="text-slate-500">Signed at:</span>{' '}
                  <span className="text-slate-200">{formatDateTime(importPreview.iat)}</span>
                </p>
                <p className="col-span-2 truncate" title={importPreview.kid}>
                  <span className="text-slate-500">Key:</span>{' '}
                  <span className="font-mono text-slate-300">{importPreview.kid}</span>
                </p>
                <p className="col-span-2 truncate" title={importPreview.jti}>
                  <span className="text-slate-500">Batch id (jti):</span>{' '}
                  <span className="font-mono text-slate-300">{importPreview.jti}</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-3 pt-1">
                <span className="text-slate-300">
                  {importPreview.total} address{importPreview.total === 1 ? '' : 'es'}
                </span>
                <span className="text-emerald-400">{importPreview.new} new</span>
                {importPreview.link > 0 && (
                  <span className="text-sky-400">{importPreview.link} existing → will be linked</span>
                )}
                {importPreview.exists > 0 && (
                  <span className="text-slate-400">{importPreview.exists} already signed</span>
                )}
                {importPreview.invalid > 0 && (
                  <span className="text-red-400">{importPreview.invalid} invalid</span>
                )}
              </div>
              {importPreview.errors.length > 0 && (
                <ul className="text-red-400 space-y-0.5 max-h-32 overflow-y-auto">
                  {importPreview.errors.map((msg, idx) => (
                    <li key={idx} className="break-all">
                      {msg}
                    </li>
                  ))}
                </ul>
              )}
              <details>
                <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
                  Addresses
                </summary>
                <div className="mt-1 max-h-48 overflow-y-auto space-y-0.5">
                  {importPreview.addresses.map((entry, idx) => (
                    <div key={`${entry.address}-${idx}`} className="flex justify-between gap-3 font-mono">
                      <span className="text-slate-300 truncate">{entry.address}</span>
                      <span
                        className={
                          entry.status === 'error'
                            ? 'text-red-400'
                            : entry.status === 'new'
                              ? 'text-emerald-400'
                              : 'text-slate-400'
                        }
                      >
                        {entry.status === 'error' ? entry.error : entry.status}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {importError && <p className="text-sm text-red-400 break-words">{importError}</p>}
          {importSuccess && <p className="text-sm text-emerald-400">{importSuccess}</p>}

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={closeImportModal} className="btn btn-secondary">
              {importSuccess ? 'Close' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={runImportPreview}
              disabled={previewLoading || importSubmitting || !importJws.trim()}
              className="btn btn-secondary disabled:opacity-60"
            >
              {previewLoading ? 'Verifying…' : 'Verify & preview'}
            </button>
            <button
              type="submit"
              disabled={
                importSubmitting ||
                !importPreview ||
                importPreview.errors.length > 0 ||
                importPreview.new + importPreview.link === 0
              }
              className="btn btn-primary disabled:opacity-60"
            >
              {importSubmitting ? 'Importing…' : 'Import'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={scanModal !== null}
        onClose={closeScanModal}
        title="Deposit scan result"
        size="lg"
      >
        {scanModal && (
          <div className="space-y-4">
            <div className="text-xs text-slate-400 space-y-1">
              <p>
                Wallet:&nbsp;
                <span className="font-mono text-slate-200 break-all">{scanModal.walletAddress}</span>
              </p>
              <p>
                Currency: <span className="text-slate-200">{scanModal.walletCurrency}</span>
              </p>
            </div>

            {scanModal.loading ? (
              <div className="flex items-center justify-center h-32 gap-3 text-slate-400">
                <div className="w-8 h-8 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                <span>Querying the blockchain…</span>
              </div>
            ) : scanModal.error ? (
              <p className="text-red-400 text-sm">{scanModal.error}</p>
            ) : scanModal.result ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="card p-3 bg-slate-800/40 text-center">
                    <p className="text-xs text-slate-500">Scanned</p>
                    <p className="text-2xl font-mono text-white">{scanModal.result.scanned}</p>
                  </div>
                  <div className="card p-3 bg-slate-800/40 text-center">
                    <p className="text-xs text-slate-500">Credited</p>
                    <p className="text-2xl font-mono text-emerald-400">{scanModal.result.credited}</p>
                  </div>
                  <div className="card p-3 bg-slate-800/40 text-center">
                    <p className="text-xs text-slate-500">Skipped</p>
                    <p className="text-2xl font-mono text-amber-400">{scanModal.result.skipped}</p>
                  </div>
                </div>

                {scanModal.result.new_deposits.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2">New deposits</h3>
                    <div className="space-y-2">
                      {scanModal.result.new_deposits.map((dep) => (
                        <div
                          key={dep.tx_hash}
                          className="card p-3 bg-slate-800/40 flex justify-between items-center gap-4"
                        >
                          <div className="min-w-0">
                            <p className="font-mono text-xs text-indigo-400 break-all">
                              {dep.tx_hash}
                            </p>
                            <p className="text-xs text-slate-400 mt-1 font-mono break-all">
                              from {dep.from}
                            </p>
                          </div>
                          <p className="font-mono text-emerald-400 whitespace-nowrap">
                            +{formatCryptoAmount(dep.amount)} {scanModal.walletCurrency}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {scanModal.result.skipped_events.length > 0 && (
                  <details className="text-xs text-slate-400">
                    <summary className="cursor-pointer hover:text-slate-200">
                      Skipped transactions ({scanModal.result.skipped_events.length})
                    </summary>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {scanModal.result.skipped_events.map((ev, idx) => (
                        <div
                          key={`${ev.tx_hash}-${idx}`}
                          className="flex justify-between gap-3 border-t border-slate-800/60 py-1"
                        >
                          <span className="font-mono text-slate-500 truncate">{ev.tx_hash}</span>
                          <span className="text-slate-400 whitespace-nowrap">{ev.reason}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {scanModal.result.scanned === 0 && (
                  <p className="text-sm text-slate-400">
                    No incoming transfers found for this address.
                  </p>
                )}
              </>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={toggleTarget !== null}
        onClose={closeToggle}
        title={toggleTarget?.is_active === false ? 'Activate wallet' : 'Deactivate wallet'}
        size="md"
      >
        {toggleTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              {toggleTarget.is_active === false
                ? 'Reactivate this deposit address for the user?'
                : 'Deactivate this deposit address?'}
            </p>
            <div className="card p-3 bg-slate-800/40 text-sm space-y-1">
              <p className="font-mono text-xs text-slate-200 break-all">{toggleTarget.address}</p>
              <p className="text-slate-400">
                {toggleTarget.currency}
                {toggleTarget.network ? ` · ${formatNetworkLabel(toggleTarget.network)}` : ''} · user #{toggleTarget.user_id}
              </p>
            </div>
            <p className="text-xs text-slate-400">
              {toggleTarget.is_active === false
                ? 'The user can only have one active address per currency and network.'
                : 'The wallet stays assigned to the user but will no longer be scanned for deposits — funds sent to it will not be credited automatically. It is hidden from the deposit UI, so a new address can be assigned.'}
            </p>
            {toggleError && <p className="text-sm text-red-400">{toggleError}</p>}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={closeToggle} className="btn btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmToggle}
                disabled={toggleSubmitting}
                className={`btn disabled:opacity-60 ${
                  toggleTarget.is_active === false ? 'btn-primary' : 'btn-danger'
                }`}
              >
                {toggleSubmitting
                  ? 'Saving…'
                  : toggleTarget.is_active === false
                    ? 'Activate'
                    : 'Deactivate'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={assignOpen} onClose={closeAssignModal} title="Assign wallet to user" size="md">
        <form onSubmit={submitAssign} className="space-y-4">
          <p className="text-sm text-slate-400">
            Attaches a free deposit address from the pool to the user. The user must not already
            have an active address for this currency and network — deactivate the old one first.
          </p>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User</label>
            {assignUserLocked ? (
              <input
                type="text"
                value={`${assignUserLabel} · #${assignUserId}`}
                disabled
                className="input py-2 opacity-60 cursor-not-allowed"
              />
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={userQuery}
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    setUserDropdownOpen(true);
                    setAssignUserId('');
                    setAssignUserLabel('');
                  }}
                  onFocus={() => setUserDropdownOpen(true)}
                  onBlur={() => window.setTimeout(() => setUserDropdownOpen(false), 150)}
                  className="input py-2"
                  placeholder="Search by name, @username, email or ID…"
                  autoComplete="off"
                  spellCheck={false}
                />
                {userDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 shadow-xl">
                    {userOptionsLoading ? (
                      <p className="px-3 py-2 text-xs text-slate-400">Searching…</p>
                    ) : userOptions.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-slate-400">No users found.</p>
                    ) : (
                      userOptions.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectAssignUser(u)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-800 transition-colors"
                        >
                          <p className="text-sm text-slate-200">
                            {u.username ? `@${u.username}` : u.full_name || u.name}
                            <span className="text-xs text-slate-500 font-mono ml-2">#{u.id}</span>
                          </p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {assignUserId && (
                  <p className="text-xs text-emerald-400 mt-1">
                    Selected: {assignUserLabel} · #{assignUserId}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
              <select
                value={assignCurrency}
                onChange={(e) => onAssignCurrencyChange(e.target.value)}
                className="input py-2"
              >
                {POOL_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Network</label>
              <select
                value={assignNetwork}
                onChange={(e) => { setAssignNetwork(e.target.value); setAssignWalletId(''); }}
                className="input py-2"
              >
                {getPoolCurrencyConfig(assignCurrency).networks.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Wallet</label>
            <select
              value={assignWalletId}
              onChange={(e) => setAssignWalletId(e.target.value)}
              className="input py-2 font-mono text-xs"
              disabled={assignPoolLoading}
            >
              <option value="">Auto — next available</option>
              {assignPool
                .filter((w) => w.network.toUpperCase() === assignNetwork.toUpperCase())
                .map((w) => (
                  <option key={w.id} value={String(w.id)}>
                    #{w.id} {w.address}
                  </option>
                ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {assignPoolLoading
                ? 'Loading free pool wallets…'
                : 'Pick a specific free address or let the system take the next one from the pool.'}
            </p>
          </div>

          {assignError && <p className="text-sm text-red-400">{assignError}</p>}
          {assignSuccess && <p className="text-sm text-emerald-400">{assignSuccess}</p>}

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={closeAssignModal} className="btn btn-secondary">
              {assignSuccess ? 'Close' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={assignSubmitting || !assignUserId}
              className="btn btn-primary disabled:opacity-60"
            >
              {assignSubmitting ? 'Assigning…' : 'Assign wallet'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={opsWallet !== null} onClose={closeOperations} title="Wallet operations" size="3xl">
        {opsWallet && (
          <div className="space-y-4">
            <div className="text-xs text-slate-400 space-y-1">
              <p>
                Wallet:&nbsp;
                <span className="font-mono text-slate-200 break-all">{opsWallet.address}</span>
              </p>
              <p>
                Currency: <span className="text-slate-200">{opsWallet.currency}</span>
                {opsWallet.network ? <> · {formatNetworkLabel(opsWallet.network)}</> : null}
                {' · '}user #{opsWallet.user_id}
              </p>
            </div>

            {opsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
              </div>
            ) : opsError ? (
              <p className="text-red-400 text-sm">{opsError}</p>
            ) : opsData ? (
              <>
                {opsData.data.length === 0 ? (
                  <p className="text-sm text-slate-400">
                    No ledger operations attributed to this address yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-800/50">
                        <tr>
                          <th className="table-header">Date</th>
                          <th className="table-header">Category</th>
                          <th className="table-header">Type</th>
                          <th className="table-header text-right">Amount</th>
                          <th className="table-header text-right">Balance after</th>
                          <th className="table-header">Reference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {opsData.data.map((op) => (
                          <tr key={op.id}>
                            <td className="table-cell text-slate-300 text-xs whitespace-nowrap">
                              {formatDateTime(op.created_at)}
                            </td>
                            <td className="table-cell">
                              <span className="badge badge-success text-[10px]">{op.category}</span>
                            </td>
                            <td className="table-cell text-slate-300 text-xs">{op.type}</td>
                            <td className="table-cell text-right font-mono text-emerald-400">
                              +{formatCryptoAmount(op.amount)} {op.currency}
                            </td>
                            <td className="table-cell text-right font-mono text-slate-300">
                              {formatCryptoAmount(op.balance_after)}
                            </td>
                            <td
                              className="table-cell font-mono text-xs text-slate-400 max-w-[160px] truncate"
                              title={op.reference_id ?? undefined}
                            >
                              {op.reference_id ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {opsData.last_page > 1 && (
                  <div className="flex justify-end">
                    <Pagination
                      currentPage={opsData.current_page}
                      lastPage={opsData.last_page}
                      onPageChange={setOpsPage}
                    />
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default WalletsPage;
